import { useEffect, useState, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { GlassCard, PageLoader, StatusBadge } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Users, Gift, Loader2, ExternalLink, KeyRound, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function AdminCustomers() {
  const [rows, setRows] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pwResult, setPwResult] = useState(null);
  const [actBusy, setActBusy] = useState(null);
  const [giftOpen, setGiftOpen] = useState(false);
  const [gift, setGift] = useState({ email: "", plan_key: "business", region: "eu-central", live: false });
  const [giftBusy, setGiftBusy] = useState(false);
  const [giftResult, setGiftResult] = useState(null);
  const [giftStatus, setGiftStatus] = useState(null);
  const poll = useRef(null);

  const load = () => api.get("/admin/customers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => () => clearInterval(poll.current), []);
  if (!rows) return <PageLoader />;

  const openDetail = (id) => { setPwResult(null); api.get(`/admin/customers/${id}`).then((r) => setDetail(r.data)); };

  const action = async (orgId, act) => {
    try { await api.post(`/admin/customers/${orgId}/action`, { action: act }); toast.success(`Done: ${act}`); openDetail(orgId); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const resetPassword = async (orgId) => {
    setActBusy("pw");
    try {
      const { data } = await api.post(`/admin/customers/${orgId}/reset-password`, {});
      setPwResult(data);
      toast.success(`Password reset for ${data.email}`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setActBusy(null); }
  };

  const deleteCustomer = async (orgId) => {
    setActBusy("del");
    try {
      const { data } = await api.post(`/admin/customers/${orgId}/delete`, {});
      if (data.ok) toast.success(`Removed ${data.name} (${data.servers_removed} servers)`);
      else toast.warning(`Removed ${data.name} with issues: ${data.errors.join("; ")}`);
      setDetail(null); load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setActBusy(null); }
  };

  const pollGift = (serverId) => {
    clearInterval(poll.current);
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/servers/${serverId}/provisioning`);
        setGiftStatus(data);
        if (["completed", "failed"].includes(data.job_status) || ["healthy", "failed"].includes(data.server_status)) {
          clearInterval(poll.current);
        }
      } catch (e) { /* keep polling */ }
    };
    tick();
    poll.current = setInterval(tick, 3000);
  };

  const doGift = async () => {
    setGiftBusy(true); setGiftResult(null); setGiftStatus(null);
    try {
      const { data } = await api.post("/admin/gift", {
        email: gift.email || null, plan_key: gift.plan_key, region: gift.region, live: gift.live,
      });
      setGiftResult(data);
      toast.success(`Gifted ${data.plan} account to ${data.email}`);
      pollGift(data.server_id);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setGiftBusy(false); }
  };

  const failed = giftStatus && (giftStatus.job_status === "failed" || giftStatus.server_status === "failed");
  const done = giftStatus && (giftStatus.job_status === "completed" || giftStatus.server_status === "healthy");

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2"><Users className="h-5 w-5 text-cyan-400" /><h1 className="font-display text-2xl font-bold text-slate-50">Customers</h1></div>
        <button onClick={() => { setGiftResult(null); setGiftStatus(null); setGiftOpen(true); }} data-testid="gift-account-btn" className="inline-flex items-center gap-2 rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#06080D] hover:bg-cyan-300"><Gift className="h-4 w-4" /> Gift Account</button>
      </div>
      <GlassCard className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-800 text-left text-xs uppercase text-slate-500">
            <tr><th className="p-3">Company</th><th className="p-3">Plan</th><th className="p-3">Servers</th><th className="p-3">Storage</th><th className="p-3">MRR</th><th className="p-3">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {rows.map((c) => (
              <tr key={c.id} onClick={() => openDetail(c.id)} data-testid={`admin-customer-${c.id}`} className="cursor-pointer hover:bg-slate-900/40">
                <td className="p-3 text-slate-100">{c.name}<div className="text-xs text-slate-500">{c.country} · {c.users} users</div></td>
                <td className="p-3 text-slate-300">{c.plan}</td>
                <td className="p-3 text-slate-300">
                  {c.server_count}
                  {c.issues > 0 && <span data-testid={`customer-issues-${c.id}`} className="ml-2 inline-flex items-center gap-1 rounded bg-rose-500/10 px-1.5 py-0.5 text-xs font-medium text-rose-400"><AlertTriangle className="h-3 w-3" />{c.issues}</span>}
                </td>
                <td className="p-3 font-mono text-slate-300">{c.used_tb}/{c.storage_tb} TB</td>
                <td className="p-3 font-mono text-emerald-300">${c.mrr}</td>
                <td className="p-3"><StatusBadge status={c.billing_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </GlassCard>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl bg-[#0D111A] border-slate-800">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="text-slate-100">{detail.org.name}</DialogTitle><DialogDescription className="text-slate-500">Manage this customer's servers, access and account.</DialogDescription></DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-slate-800 bg-[#06080D] p-3"><div className="text-xs text-slate-500">Billing</div><StatusBadge status={detail.org.billing_status} /></div>
                <div className="rounded-lg border border-slate-800 bg-[#06080D] p-3"><div className="text-xs text-slate-500">Plan</div><div className="text-slate-200">{detail.subscription?.plan_key || "—"}</div></div>
              </div>
              <div>
                <div className="mb-1 text-xs uppercase text-slate-500">Servers</div>
                {detail.servers.length === 0 && <div className="text-sm text-slate-500">No servers.</div>}
                {detail.servers.map((s) => (
                  <div key={s.id} className="mb-1 rounded-lg border border-slate-800 bg-[#06080D] p-2 text-sm">
                    <div className="flex items-center justify-between"><span className="font-mono text-xs text-slate-300">{s.hostname}</span><StatusBadge status={s.status} /></div>
                    {s.provision_error && (s.status === "failed" || s.status === "action_required") && (
                      <div data-testid={`detail-error-${s.id}`} className="mt-1 flex items-start gap-1.5 text-xs text-rose-400/90"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{s.provision_error}</div>
                    )}
                  </div>
                ))}
              </div>

              {pwResult && (
                <div data-testid="pw-reset-result" className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-200">
                  New temporary password for <b>{pwResult.email}</b>: <span className="font-mono text-amber-100">{pwResult.temp_password}</span> <span className="text-amber-300/70">(shown once — copy it now)</span>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {[["suspend", "Suspend"], ["unsuspend", "Unsuspend"], ["reset_mfa", "Reset MFA"]].map(([a, l]) => (
                  <button key={a} onClick={() => action(detail.org.id, a)} data-testid={`admin-action-${a}`} className="rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-200 hover:border-cyan-500/50">{l}</button>
                ))}
                <button onClick={() => resetPassword(detail.org.id)} disabled={actBusy === "pw"} data-testid="admin-reset-password" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-200 hover:border-cyan-500/50 disabled:opacity-50">
                  {actBusy === "pw" ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Reset password
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid="admin-delete-customer" className="inline-flex items-center gap-1.5 rounded-lg border border-rose-500/40 px-3 py-1.5 text-sm text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /> Delete customer</button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="delete-customer-dialog" className="border-slate-800 bg-slate-950">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-slate-50">Delete {detail.org.name}?</AlertDialogTitle>
                      <AlertDialogDescription className="text-slate-400">
                        This permanently removes the organization, all its users, subscriptions, tickets, alerts, and tears down every server's cloud resources (Hetzner + B2). This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-customer-cancel" className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800">Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid="delete-customer-confirm" onClick={() => deleteCustomer(detail.org.id)} className="bg-rose-500 text-white hover:bg-rose-600">Delete permanently</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={giftOpen} onOpenChange={(v) => { setGiftOpen(v); if (!v) clearInterval(poll.current); }}>
        <DialogContent className="bg-[#0D111A] border-slate-800">
          <DialogHeader><DialogTitle className="text-slate-100">Gift a backup account</DialogTitle><DialogDescription className="text-slate-500">Comp a plan and provision a backup server without Stripe.</DialogDescription></DialogHeader>
          {!giftResult ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-400">Comp a plan (no Stripe charge) and provision a backup server. Leave email blank to gift to yourself.</p>
              <Input placeholder="Email (blank = your admin account)" value={gift.email} onChange={(e) => setGift({ ...gift, email: e.target.value })} data-testid="gift-email" className="bg-slate-950 border-slate-700" />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-500">Plan</label>
                  <select value={gift.plan_key} onChange={(e) => setGift({ ...gift, plan_key: e.target.value })} data-testid="gift-plan" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
                    <option value="starter">Starter (1 TB)</option><option value="pro">Pro (2 TB)</option><option value="business">Business (5 TB)</option><option value="business10">Business 10 (10 TB)</option>
                  </select></div>
                <div><label className="text-xs text-slate-500">Region</label>
                  <select value={gift.region} onChange={(e) => setGift({ ...gift, region: e.target.value })} data-testid="gift-region" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
                    <option value="us-west">US West</option><option value="us-central">US Central</option><option value="us-east">US East</option><option value="eu-central">Europe</option>
                  </select></div>
              </div>
              <label className="flex items-center justify-between rounded-lg border border-slate-800 bg-[#06080D] p-3 text-sm text-slate-300">
                <span>Provision a <b className="text-cyan-300">LIVE</b> Hetzner+B2 server <span className="text-slate-500">(real cost; installs PBS ~10 min). Off = DEV MOCK.</span></span>
                <Switch checked={gift.live} onCheckedChange={(v) => setGift({ ...gift, live: v })} data-testid="gift-live" />
              </label>
              <button onClick={doGift} disabled={giftBusy} data-testid="gift-submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-cyan-400 py-2.5 font-semibold text-[#06080D] disabled:opacity-60">
                {giftBusy && <Loader2 className="h-4 w-4 animate-spin" />} Gift & provision
              </button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="gift-result">
              <div className="rounded-lg border border-slate-700 bg-[#06080D] p-4 text-sm text-slate-300">
                Gifted <b className="text-slate-100">{giftResult.plan}</b> to <b className="text-slate-100">{giftResult.email}</b> — {giftResult.mode === "live" ? "LIVE" : "DEV MOCK"} server.
              </div>

              {giftResult.temp_password && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-200">
                  New account. Temporary password: <span className="font-mono text-amber-100">{giftResult.temp_password}</span> <span className="text-amber-300/70">(shown once — save it)</span>
                </div>
              )}

              <div data-testid="gift-status" className={`rounded-lg border p-3 text-sm ${failed ? "border-rose-500/30 bg-rose-500/5 text-rose-200" : done ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-200" : "border-cyan-500/30 bg-cyan-500/5 text-cyan-200"}`}>
                <div className="flex items-center gap-2">
                  {failed ? <AlertTriangle className="h-4 w-4" /> : done ? <CheckCircle2 className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />}
                  <span className="font-medium">
                    {failed ? "Provisioning failed" : done ? "Server ready" : (giftStatus?.current_step || "Provisioning…")}
                  </span>
                  {giftStatus?.job_status && <StatusBadge status={giftStatus.job_status} />}
                </div>
                {failed && (giftStatus?.provision_error || giftStatus?.error) && (
                  <div data-testid="gift-error-reason" className="mt-2 font-mono text-xs text-rose-300/90">{giftStatus.provision_error || giftStatus.error}</div>
                )}
              </div>

              {giftStatus?.logs?.length > 0 && (
                <div data-testid="gift-logs" className="max-h-56 overflow-y-auto rounded-lg border border-slate-800 bg-black/50 p-2.5 font-mono text-[11px] leading-relaxed">
                  {giftStatus.logs.map((l, i) => (
                    <div key={i} className={l.level === "error" ? "text-rose-400" : l.level === "warn" ? "text-amber-300" : "text-slate-400"}>
                      <span className="text-slate-600">{new Date(l.ts).toLocaleTimeString()} </span>{l.message}
                    </div>
                  ))}
                </div>
              )}

              <a href={`/app/servers/${giftResult.server_id}`} className="inline-flex items-center gap-2 rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#06080D]" data-testid="gift-open-server">
                Open server & connect Proxmox <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
